import { useState } from 'react'
import { Building2, Check, Plus, X } from 'lucide-react'
import { Badge, Dropdown, Tooltip } from '@/components/ui'
import { useT } from '@/internationalization/i18n'
import type { PatronRef } from '@/types'
import { usePatrons, useSetMeetingPatrons } from '../hooks'

/**
 * The patrons a meeting is about, as removable chips plus an add menu.
 *
 * TWO KINDS OF CHIP, AND THE DIFFERENCE IS NOT COSMETIC:
 *
 *  - `invite` — derived from an external attendee's email domain. It has NO
 *    remove button, because there is nothing here to remove: the link is a
 *    read-time consequence of the invite, and detaching it means taking the
 *    domain off the patron in Settings. A chip with an × that silently did
 *    nothing would be worse than no × at all.
 *  - `manual` — somebody attached it. Removable right here.
 *
 * Attaching by hand is the ONLY signal an internal discussion about a patron can
 * ever have, which is why this is a MEMBER action rather than an admin one: the
 * people who were in the meeting are the ones who know.
 */
export function MeetingPatrons({
  meetingId,
  patrons,
  compact = false,
}: {
  meetingId: string
  patrons: PatronRef[]
  compact?: boolean
}) {
  const t = useT()
  const { data: available } = usePatrons()
  const setPatrons = useSetMeetingPatrons()
  const [busy, setBusy] = useState(false)

  const manualIds = patrons.filter((c) => c.source === 'manual').map((c) => c.id)

  const save = (next: string[]) => {
    setBusy(true)
    setPatrons.mutate(
      { meetingId, patronIds: next },
      { onSettled: () => setBusy(false) },
    )
  }

  const attached = new Set(patrons.map((c) => c.id))
  const addable = (available ?? []).filter((c) => !attached.has(c.id))

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {patrons.length === 0 && !compact && (
        <span className="text-sm text-fg-subtle">{t('meetingPatrons.none')}</span>
      )}

      {patrons.map((patron) => (
        <Tooltip
          key={patron.id}
          content={
            patron.source === 'invite'
              ? t('meetingPatrons.fromInviteTip', { name: patron.name })
              : t('meetingPatrons.manualTip', { name: patron.name })
          }
        >
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-xs text-fg">
            <Building2 aria-hidden className="h-3 w-3 text-fg-subtle" />
            {patron.name}
            {patron.source === 'invite' ? (
              <Badge tone="neutral" size="sm">
                {t('meetingPatrons.fromInvite')}
              </Badge>
            ) : (
              <button
                type="button"
                aria-label={t('meetingPatrons.remove', { name: patron.name })}
                disabled={busy}
                onClick={() => save(manualIds.filter((id) => id !== patron.id))}
                className="rounded-full p-0.5 hover:bg-surface disabled:opacity-50"
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            )}
          </span>
        </Tooltip>
      ))}

      <Dropdown
        heading={t('meetingPatrons.addHeading')}
        trigger={({ toggle }) => (
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-fg-muted hover:border-brand hover:text-fg disabled:opacity-50"
          >
            <Plus aria-hidden className="h-3 w-3" />
            {t('meetingPatrons.addTrigger')}
          </button>
        )}
        items={
          addable.length > 0
            ? addable.map((patron) => ({
                id: patron.id,
                label: patron.name,
                icon: <Check className="opacity-0" />,
                onSelect: () => save([...manualIds, patron.id]),
              }))
            : [
                {
                  id: 'none',
                  label:
                    (available ?? []).length === 0
                      ? t('meetingPatrons.emptyHint')
                      : t('meetingPatrons.allAttached'),
                  disabled: true,
                },
              ]
        }
      />
    </div>
  )
}
